import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Message } from '../fixtures/domain.js';
import { cidParts } from '../lib/bridge.js';
import { drawSummary } from '../lib/facade.js';
import {
  needsRedraw,
  pendingAfter,
  summarisable,
  type ConversationSummary,
  type DrawnSummary,
} from './doc-rules.js';

export interface UseConversationSummary {
  summary: ConversationSummary | null;
  /**
   * A fresh draw waiting on the employer. Shown in place of `summary` so the box
   * shows the proposal, not the record, until it is kept or discarded. Null while
   * a pass is still in flight — the draw arrives whole (the server does not
   * stream), so there is nothing to show until it lands.
   */
  proposal: string | null;
  /** Messages said since the summary was drawn. Zero when it is current. */
  pendingCount: number;
  /** A pass is in flight. The panel runs the beam on this. */
  generating: boolean;
  /** Why the last attempt failed. Cleared on the next try. */
  error: string | null;
  /** Something changed since the summary was kept: a message arrived, or the note
   *  was saved. What puts the button on screen. */
  stale: boolean;
  /** There is something to draw from. */
  canDraw: boolean;
  draw: () => void;
  /** Abandon a pass in flight. What was on screen stays on screen. */
  cancel: () => void;
  /** Accept the draw. Only now does it become the summary. */
  keep: () => void;
  /** Refuse the draw and go back to what was there. */
  discard: () => void;
}

/**
 * The conversation's summary, and the operations that change it.
 *
 * Drawn on request, never on its own — a model call is not worth spending on
 * every chat the employer clicks past, and the decision should not be taken from
 * them mid-read. What is automatic is *noticing*: `stale` goes true the moment a
 * message lands or a note is saved, which is what puts the button there.
 *
 * A draw does not replace anything by itself. It arrives as a `proposal` the
 * employer keeps or discards, and until they keep it the stored summary is what
 * the panel shows. The model writes, a person decides — the same rule the app
 * follows everywhere.
 *
 * Unlike the mock, the draw runs server-side (the OpenRouter key cannot ship in
 * the client bundle): one request returns the whole `{ text, coversUpTo }`, and
 * the watermark is the server's, not one the client computes. `messages` is still
 * read here, but only to notice staleness — never to feed the draw, which reads
 * the transcript upstream.
 */
export function useConversationSummary(
  channelId: string,
  messages: Message[],
  summary: ConversationSummary | null,
  noteText: string,
  saveSummary: (drawn: DrawnSummary) => Promise<void>,
): UseConversationSummary {
  const [generating, setGenerating] = useState(false);
  const [pending, setPending] = useState<DrawnSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * The pass in flight, so cancelling releases the request instead of leaving it
   * to land and be discarded. The reference client aborts for the same reason.
   *
   * This stops the client waiting; it does NOT yet stop the spend. The abort
   * ends at the BFF: `upstreamFetch` takes no inbound signal, so the handler
   * runs on to the backend and OpenRouter, and Generate/Cancel/Generate still
   * bills one completion per press. Closing that needs the inbound signal
   * threaded through to the upstream call — the mock has no such gap because it
   * calls the model directly, over one hop.
   */
  const inFlight = useRef<AbortController | null>(null);

  // Aborting on unmount and on a channel change, for the same reason: a pass
  // whose panel has gone is a request nobody is waiting for.
  useEffect(() => {
    return () => {
      inFlight.current?.abort();
      inFlight.current = null;
    };
  }, [channelId]);

  const { covered, pendingCount, stale } = useMemo(
    () => ({
      covered: summarisable(messages),
      pendingCount: pendingAfter(messages, summary).length,
      stale: needsRedraw(messages, summary, noteText),
    }),
    [messages, summary, noteText],
  );

  const draw = useCallback(() => {
    if (covered.length === 0) return;
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    setGenerating(true);
    setError(null);
    // The note as it stood when the draw began, captured now rather than read
    // again on keep: a message or edit landing mid-draw must not be stamped as
    // covered by a summary written before it arrived.
    const drawnWithNote = noteText;
    // The panel holds a Stream cid; the upstream keys channels by the bare id, so
    // strip it here as the note calls do (useDocs).
    void drawSummary({ channelId: cidParts(channelId).channelId }, controller.signal)
      .then((drawn) => {
        if (controller.signal.aborted) return;
        setPending({ text: drawn.text, coversUpTo: drawn.coversUpTo, drawnWithNote });
        setGenerating(false);
      })
      .catch((cause) => {
        // An abort is not a failure: the employer asked for it, and the box is
        // already back to what it was showing.
        if (controller.signal.aborted) return;
        setGenerating(false);
        setError(drawError(cause));
      });
  }, [channelId, covered.length, noteText]);

  const cancel = useCallback(() => {
    // Abort the request, not just its result. What was on screen before the
    // press stays on screen.
    inFlight.current?.abort();
    inFlight.current = null;
    setGenerating(false);
  }, []);

  const keep = useCallback(() => {
    if (!pending) return;
    const drawn = pending;
    setPending(null);
    void saveSummary(drawn).catch(() => {
      // The write failed; put the proposal back so it can be kept again.
      setPending(drawn);
      setError('Could not save the summary.');
    });
  }, [pending, saveSummary]);

  const discard = useCallback(() => setPending(null), []);

  return {
    summary,
    proposal: pending?.text ?? null,
    pendingCount,
    generating,
    error,
    stale,
    canDraw: covered.length > 0,
    draw,
    cancel,
    keep,
    discard,
  };
}

/** A draw failure, in the caller's terms. The BFF tells the "nothing to summarize"
 *  case apart from a generic one; everything else is the same "could not draw"
 *  (a model outage arrives as the generic retryable upstream error). */
function drawError(cause: unknown): string {
  const code = (cause as { code?: string } | null)?.code;
  if (code === 'nothing_to_summarize') return 'There is nothing to summarize yet.';
  return 'Could not draw the summary.';
}
